/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {useRef, useState} from 'react';
import {DeleteOutlined, SearchOutlined} from "@ant-design/icons";
import {Button, Input, InputRef, Space, Table, TableColumnsType, TableColumnType} from "antd";
import {FilterDropdownProps} from "antd/es/table/interface";
import styles from './BlockList.module.scss';
import {BaechaBlockRes} from "../../../UserList.interface";
import {formatKorDate} from "../../../../../util/date";
import Highlighter from "react-highlight-words";

type DataIndex = keyof BaechaBlockRes;

interface BlockListProps {
	blockList: BaechaBlockRes[];
	onDelete: (e: any, id: string) => any;
}

const getPlaceHolder = (dataIndex: string) => {
	if (dataIndex === 'targetPhoneNum') return '차단전화번호 검색';
	if (dataIndex === 'targetGroupName') return '차단배차방 검색';
	return '';
};

const BlockList = ({ blockList, onDelete }: BlockListProps) => {
	const [searchText, setSearchText] = useState('');
	const [searchedColumn, setSearchedColumn] = useState('');
	const searchInput = useRef<InputRef>(null);

	const handleSearch = (
		selectedKeys: string[],
		confirm: FilterDropdownProps['confirm'],
		dataIndex: DataIndex,
	) => {
		confirm();
		setSearchText(selectedKeys[0]);
		setSearchedColumn(dataIndex);
	};

	const handleReset = (clearFilters: () => void) => {
		clearFilters();
		setSearchText('');
	};

	const getColumnSearchProps = (dataIndex: DataIndex): TableColumnType<BaechaBlockRes> => ({
		filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }) => (
			<div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
				<Input
					ref={searchInput}
					placeholder={getPlaceHolder(dataIndex)}
					value={selectedKeys[0]}
					onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
					onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
					style={{ marginBottom: 8, display: 'block' }}
				/>
				<Space>
					<Button
						type="primary"
						onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
						icon={<SearchOutlined />}
						size="small"
						style={{ width: 90 }}
					>
						검색
					</Button>
					<Button
						onClick={() => clearFilters && handleReset(clearFilters)}
						size="small"
						style={{ width: 90 }}
					>
						초기화
					</Button>
					<Button
						type="link"
						size="small"
						onClick={() => {
							confirm({ closeDropdown: false });
							setSearchText((selectedKeys as string[])[0]);
							setSearchedColumn(dataIndex);
						}}
					>
						적용
					</Button>
					<Button
						type="link"
						size="small"
						onClick={() => {
							close();
						}}
					>
						닫기
					</Button>
				</Space>
			</div>
		),
		filterIcon: (filtered: boolean) => (
			<SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
		),
		onFilter: (value, record) =>
			(record[dataIndex] ?? '')
				.toString()
				.toLowerCase()
				.includes((value as string).toLowerCase()),
		filterDropdownProps: {
			onOpenChange(open) {
				if (open) {
					setTimeout(() => searchInput.current?.select(), 100);
				}
			},
		},
		render: (text) =>
			searchedColumn === dataIndex ? (
				<Highlighter
					highlightStyle={{ backgroundColor: '#ffc069', padding: 0 }}
					searchWords={[searchText]}
					autoEscape
					textToHighlight={text ? text.toString() : ''}
				/>
			) : (
				text
			),
	});

	const shadedCell = { style: {backgroundColor: '#f5f5f5'} };

	const columns: TableColumnsType<BaechaBlockRes> = [
		{
			title: '차단일',
			dataIndex: 'createDate',
			key: 'createDate',
			width: 80,
			align: 'center',
			render: (value: any, record: BaechaBlockRes) => formatKorDate(value),
		},
		{
			title: '배차방',
			dataIndex: 'groupName',
			key: 'groupName',
			width: 100,
			align: 'center',
			render: (value: any, record: BaechaBlockRes) => value,
		},
		{
			title: '차단전화번호',
			dataIndex: 'targetPhoneNum',
			key: 'targetPhoneNum',
			width: 80,
			align: 'center',
			onCell: () => shadedCell,
			render: (value: any, record: BaechaBlockRes) => value,
			...getColumnSearchProps('targetPhoneNum'),
		},
		{
			title: '차단배차방',
			dataIndex: 'targetGroupName',
			key: 'targetGroupName',
			width: 100,
			align: 'center',
			onCell: () => shadedCell,
			render: (value: any) => value,
			...getColumnSearchProps('targetGroupName'),

		},
		{
			title: '사유',
			dataIndex: 'reason',
			key: 'reason',
			width: 150,
			align: 'center',
			render: (value: any) => value,
		},
		{
			title: '차단 해제',
			dataIndex: 'deleteBtn',
			key: 'deleteBtn',
			width: 50,
			align: 'center',
			render: (value: any, record: BaechaBlockRes) => {
				return (
					<Button
						shape='circle'
						type='primary'
						danger
						onClick={(e) => onDelete(e, record.id)}
					>
						<DeleteOutlined />
					</Button>
				)
			},
		},
	];

	return (
		<div className={styles.wrap}>
			<Table
				bordered
				columns={columns}
				dataSource={blockList}
				rowKey="id"
				size="small"
				scroll={{ x: 'max-content' }}
				pagination={false}
				title={() => (
					<div style={{ fontSize: '16px', fontWeight: 'bold' }}>
						{`차단 목록 (전화번호: ${blockList.filter((item) => item.targetPhoneNum).length} / 배차방: ${blockList.filter((item) => item.targetGroupId).length})`}
					</div>
				)}
			/>
		</div>
	);
};

export default BlockList;
